import React, { useRef, useState } from 'react';
import { Box, useMediaQuery, useTheme } from '@material-ui/core';
import PlaylistAddOutlinedIcon from '@material-ui/icons/PlaylistAddOutlined';
import { emailIsValidRe, wait } from 'globals/utils';
import defaultLocales from 'globals/locales/default';
import { UserRole } from 'globals/types';
import FsPendingButton from 'ui-elements/buttons/FsPendingButton';
import FsFormGridWrapper from 'ui-elements/form-elements/FsFormGridWrapper';
import FsFormGridItem from 'ui-elements/form-elements/FsFormGridItem';
import FsButton from 'ui-elements/buttons/FsButton';
import FsNextArrowIcon from 'ui-elements/icons/FsNextArrow';
import FsBody from 'ui-elements/typography/FsBody';
import FsConfirmationDialog from 'ui-elements/modals/FsConfirmationDialog';
import FsBodyBold from 'ui-elements/typography/FsBodyBold';
import { AddNewButton } from './FsInviteNewUserRepetitiveForm.style-elements';
import InviteNewUserSingleRow from './InviteNewUserSingleRow';
import { InviteNewUserSingleRowProps } from './InviteNewUserSingleRow/InviteNewUserSingleRow';
import {
  skipButtonStyle,
  mainButtonStyle,
} from './FsInviteNewUserRepetitiveForm.style';

interface OnSaveProps {
  invitations: InviteNewUserSingleRowProps[];
}

export interface FsInviteNewUserRepetitiveFormProps {
  rows: InviteNewUserSingleRowProps[];
  onSave?: (props?: OnSaveProps) => void;
}

interface AdditionalIdx {
  id: number;
}

const FsInviteNewUserRepetitiveForm: React.FC<FsInviteNewUserRepetitiveFormProps> = ({
  rows,
  onSave,
}) => {
  const formEl = useRef<HTMLFormElement>(null);
  const [originRows, setOriginRows] = useState<
    Array<InviteNewUserSingleRowProps & AdditionalIdx>
  >(
    rows.map((row, idx) => ({
      id: idx,
      ...row,
    }))
  );
  const [pending, setPending] = useState<boolean>(false);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState<boolean>(
    false
  );
  const suspiciousEmails = originRows.filter(
    ({ email }) => !!email && !email.match(emailIsValidRe)
  );
  const theme = useTheme();
  const isUpSm = useMediaQuery(theme.breakpoints.up('sm'), {
    noSsr: true,
  });

  const handleAdd = () => {
    setOriginRows((currentRows) => [
      ...currentRows,
      {
        id: (currentRows?.[currentRows.length - 1]?.id || 0) + 1,
        role: UserRole.USER,
      },
    ]);
  };

  const handleRemove = (selectedId: number) => {
    setOriginRows((currentRows) =>
      currentRows.filter(({ id }) => id !== selectedId)
    );
  };

  const handleChange = (
    changedField: InviteNewUserSingleRowProps & AdditionalIdx
  ) => {
    setOriginRows((currentRows) =>
      currentRows.map((field) => {
        return field.id === changedField.id ? changedField : field;
      })
    );
  };

  const getCleanListOfEmails = () => {
    return originRows
      .filter(
        ({ email, role }) => !!email && !!role && !!email.match(emailIsValidRe)
      )
      .map(({ email, role }) => ({
        email,
        role,
      }));
  };

  const handleConfirm = async () => {
    setShowConfirmationDialog(false);
    setPending(true);
    await wait(1000);
    setPending(false);
    const payload = getCleanListOfEmails();
    // eslint-disable-next-line no-console
    console.log('UPDATE:', payload);
    if (onSave)
      onSave({
        invitations: payload,
      });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPending(true);
    await wait(1000);
    if (suspiciousEmails.length > 0) {
      setShowConfirmationDialog(true);
      setPending(false);
      return;
    }
    await handleConfirm();
  };

  const handleSkip = () => {
    if (onSave) onSave();
  };

  return (
    <Box>
      <form onSubmit={handleSubmit} ref={formEl}>
        <Box marginBottom={2}>
          <Box>
            {/* <pre>{JSON.stringify(originRows, null, 2)}</pre> */}
            {originRows.map(({ id, ...otherProps }) => (
              <InviteNewUserSingleRow
                {...otherProps}
                key={id}
                data-test={originRows.length}
                isOnlyOneRow={originRows.length <= 1}
                onRemove={() => handleRemove(id)}
                onChange={(changedField) =>
                  handleChange({ id, ...changedField })
                }
              />
            ))}
            <FsFormGridWrapper>
              <FsFormGridItem>
                <AddNewButton
                  fullWidth
                  color="primary"
                  startIcon={<PlaylistAddOutlinedIcon />}
                  onClick={handleAdd}
                >
                  {defaultLocales.addNew}
                </AddNewButton>
              </FsFormGridItem>
              <FsFormGridItem style={{ flexGrow: 0, minWidth: 48 }} />
            </FsFormGridWrapper>
          </Box>
        </Box>
        <FsPendingButton
          type="submit"
          variant="contained"
          color="primary"
          style={mainButtonStyle()}
          pending={pending}
          endIcon={<FsNextArrowIcon />}
          width={160}
          spaceBetween
        >
          {defaultLocales.continue}
        </FsPendingButton>
        <Box ml={2} display="inline-block">
          <FsButton onClick={handleSkip} style={skipButtonStyle()}>
            {isUpSm ? defaultLocales.doItLater : defaultLocales.skip}
          </FsButton>
        </Box>
      </form>

      <FsConfirmationDialog
        title={defaultLocales.confirmInvitations}
        showModal={showConfirmationDialog}
        setShowModal={() => setShowConfirmationDialog(false)}
        actionButtons={[
          <FsButton key={1} onClick={handleConfirm}>
            {isUpSm
              ? `${
                  defaultLocales.yes
                }, ${defaultLocales.continue.toLowerCase()}`
              : defaultLocales.continue}
          </FsButton>,
          <FsButton
            key={2}
            onClick={() => setShowConfirmationDialog(false)}
            color="primary"
            variant="contained"
          >
            {defaultLocales.no}
          </FsButton>,
        ]}
      >
        <FsBody>{defaultLocales.sendInvitationsInvalidEmailsQuestion}</FsBody>
        <Box mt={2}>
          <FsBodyBold>{defaultLocales.suspiciousEmails}:</FsBodyBold>
          {suspiciousEmails.map(({ id, email }) => (
            <FsBody key={id}> - {email}</FsBody>
          ))}
        </Box>
      </FsConfirmationDialog>
    </Box>
  );
};

export default FsInviteNewUserRepetitiveForm;
